import { distance, pointToSegmentDistance } from './geometry';
const HIT_TOLERANCE = 0.015;
/**
 * Tests if a point is "on" or "near" a shape's stroke.
 * All coordinates are in normalized (0-1) space.
 */
export function hitTestShape(shape, px, py) {
    switch (shape.type) {
        case 'box':
            return hitTestBox(shape, px, py);
        case 'circle':
            return hitTestEllipse(shape, px, py);
        case 'line':
        case 'arrow':
            return hitTestArrow(shape, px, py);
        case 'freeform':
            return hitTestFreeform(shape, px, py);
        case 'text':
            return hitTestTextFill(shape, px, py);
        default:
            return false;
    }
}
function hitTestBox(shape, px, py) {
    const { x, y, width, height } = shape;
    // Check distance to each of the 4 edges
    const edges = [
        [x, y, x + width, y], // top
        [x + width, y, x + width, y + height], // right
        [x + width, y + height, x, y + height], // bottom
        [x, y + height, x, y], // left
    ];
    for (const [x1, y1, x2, y2] of edges) {
        if (pointToSegmentDistance(px, py, x1, y1, x2, y2) < HIT_TOLERANCE) {
            return true;
        }
    }
    return false;
}
/** Text shapes are selectable by clicking anywhere inside their bounding box. */
function hitTestTextFill(shape, px, py) {
    return (px >= shape.x && px <= shape.x + shape.width &&
        py >= shape.y && py <= shape.y + shape.height);
}
function hitTestEllipse(shape, px, py) {
    const { cx, cy, rx, ry } = shape;
    if (rx === 0 || ry === 0)
        return false;
    // Normalized distance from center
    const dx = (px - cx) / rx;
    const dy = (py - cy) / ry;
    const normalizedDist = Math.sqrt(dx * dx + dy * dy);
    // Hit if close to the ellipse boundary (normalized distance ~ 1)
    const toleranceNormalized = HIT_TOLERANCE / Math.min(rx, ry);
    return Math.abs(normalizedDist - 1) < toleranceNormalized;
}
function hitTestArrow(shape, px, py) {
    return pointToSegmentDistance(px, py, shape.x1, shape.y1, shape.x2, shape.y2) < HIT_TOLERANCE;
}
function hitTestFreeform(shape, px, py) {
    const { points } = shape;
    if (points.length === 0)
        return false;
    if (points.length === 1) {
        return distance(px, py, points[0].x, points[0].y) < HIT_TOLERANCE;
    }
    for (let i = 0; i < points.length - 1; i++) {
        const d = pointToSegmentDistance(px, py, points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
        if (d < HIT_TOLERANCE)
            return true;
    }
    return false;
}
import { getShapeBounds } from '../hooks/useShapeResize';
const HANDLE_HIT_RADIUS_PX = 12;
const ROTATE_DOT_OFFSET_PX = 20;
const ROTATE_DOT_HIT_RADIUS_PX = 6;
const BODY_STROKE_PX = 8;
const MIN_SHAPE_SIZE_FOR_ROTATION = 30;
const ALL_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CORNER_HANDLES_LIST = ['nw', 'ne', 'se', 'sw'];
const CURSOR_MAP = {
    nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize',
    se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
};
function handlePosition(handle, bounds) {
    const { x, y, width, height } = bounds;
    switch (handle) {
        case 'nw': return { x, y };
        case 'n': return { x: x + width / 2, y };
        case 'ne': return { x: x + width, y };
        case 'e': return { x: x + width, y: y + height / 2 };
        case 'se': return { x: x + width, y: y + height };
        case 's': return { x: x + width / 2, y: y + height };
        case 'sw': return { x, y: y + height };
        case 'w': return { x, y: y + height / 2 };
    }
}
/**
 * Determines the cursor to show based on pointer position relative to
 * selected annotation shapes. Uses coordinate math instead of SVG hit
 * testing to avoid Firefox bugs with preserveAspectRatio="none".
 *
 * Uses the SVG element's actual rendered screen dimensions (from
 * getBoundingClientRect) to ensure hit areas exactly match the rendered
 * element positions, regardless of CSS transforms or zoom.
 *
 * Returns null if the pointer is not over any interactive element,
 * meaning the default tool cursor should be used.
 */
export function getAnnotationCursor(pt, selectedShapes, outlineShapes, screenWidth, screenHeight) {
    const hit = hitTestAnnotationUI(pt, selectedShapes, outlineShapes, screenWidth, screenHeight);
    return hit ? hit.cursor : null;
}
/**
 * Full JS-based hit testing for annotation UI controls (handles, rotation
 * dots, body drag areas). Returns the hit result with shape ID, type, and
 * cursor, or null if no UI element was hit.
 *
 * Priority: resize handles > rotation dots > body drag area.
 *
 * @param pt - Normalized 0-1 pointer position within the SVG viewBox.
 * @param selectedShapes - Shapes currently selected (have handles + rotation dots).
 * @param outlineShapes - Shapes showing outline (selected + hovered, have body drag area).
 * @param screenWidth - SVG element's actual rendered width in screen pixels (getBoundingClientRect().width).
 * @param screenHeight - SVG element's actual rendered height in screen pixels (getBoundingClientRect().height).
 */
export function hitTestAnnotationUI(pt, selectedShapes, outlineShapes, screenWidth, screenHeight) {
    // Convert pixel-based constants to normalized 0-1 space using actual screen dimensions.
    const hitRx = screenWidth > 0 ? HANDLE_HIT_RADIUS_PX / screenWidth : 0;
    const hitRy = screenHeight > 0 ? HANDLE_HIT_RADIUS_PX / screenHeight : 0;
    const dotHitRx = screenWidth > 0 ? ROTATE_DOT_HIT_RADIUS_PX / screenWidth : 0;
    const dotHitRy = screenHeight > 0 ? ROTATE_DOT_HIT_RADIUS_PX / screenHeight : 0;
    const dotOffX = screenWidth > 0 ? ROTATE_DOT_OFFSET_PX / screenWidth : 0;
    const dotOffY = screenHeight > 0 ? ROTATE_DOT_OFFSET_PX / screenHeight : 0;
    const bodyExtX = screenWidth > 0 ? BODY_STROKE_PX / screenWidth : 0;
    const bodyExtY = screenHeight > 0 ? BODY_STROKE_PX / screenHeight : 0;
    for (const shape of selectedShapes) {
        const bounds = getShapeBounds(shape);
        // When the shape is rotated, the visual handles/dots are rendered at
        // rotated positions (via SVG matrix transform). Instead of rotating
        // every handle point, inverse-rotate the pointer into the shape's
        // local (unrotated) coordinate space. This is cheaper and simpler.
        const localPt = inverseRotatePoint(pt, shape, bounds, screenWidth, screenHeight);
        // Resize handles (highest priority)
        for (const handle of ALL_HANDLES) {
            const pos = handlePosition(handle, bounds);
            if (Math.abs(localPt.x - pos.x) <= hitRx && Math.abs(localPt.y - pos.y) <= hitRy) {
                return { type: 'handle', shapeId: shape.id, handle, cursor: CURSOR_MAP[handle] };
            }
        }
        // Rotation dots (only when shape is large enough on screen)
        const sw = bounds.width * screenWidth;
        const sh = bounds.height * screenHeight;
        if (sw > MIN_SHAPE_SIZE_FOR_ROTATION && sh > MIN_SHAPE_SIZE_FOR_ROTATION) {
            for (const corner of CORNER_HANDLES_LIST) {
                const pos = handlePosition(corner, bounds);
                const dirX = corner.includes('e') ? 1 : -1;
                const dirY = corner.includes('s') ? 1 : -1;
                const dx = pos.x + dotOffX * dirX;
                const dy = pos.y + dotOffY * dirY;
                if (Math.abs(localPt.x - dx) <= dotHitRx && Math.abs(localPt.y - dy) <= dotHitRy) {
                    return { type: 'rotate', shapeId: shape.id, cursor: 'grab' };
                }
            }
        }
    }
    // Body move area (selected + hovered shapes)
    for (const shape of outlineShapes) {
        const bounds = getShapeBounds(shape);
        const localPt = inverseRotatePoint(pt, shape, bounds, screenWidth, screenHeight);
        if (localPt.x >= bounds.x - bodyExtX && localPt.x <= bounds.x + bounds.width + bodyExtX &&
            localPt.y >= bounds.y - bodyExtY && localPt.y <= bounds.y + bounds.height + bodyExtY) {
            return { type: 'body', shapeId: shape.id, cursor: 'move' };
        }
    }
    return null;
}
/**
 * Inverse-rotate a pointer position from screen space into a shape's
 * local (unrotated) coordinate space. Rotation happens in screen pixels
 * (to match the SVG matrix transform used for visual rendering), then
 * converts back to normalized 0-1 coords.
 *
 * When the shape has no rotation, returns the point unchanged.
 */
function inverseRotatePoint(pt, shape, bounds, screenWidth, screenHeight) {
    const rotation = shape.rotation ?? 0;
    if (rotation === 0)
        return pt;
    // Center of shape in normalized coords
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    // Convert to screen pixels, rotate by -angle, convert back.
    // This matches the inverse of computeRotationTransform / rotatePointScreenSpace.
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = (pt.x - cx) * screenWidth;
    const dy = (pt.y - cy) * screenHeight;
    return {
        x: cx + (dx * cos - dy * sin) / screenWidth,
        y: cy + (dx * sin + dy * cos) / screenHeight,
    };
}
/**
 * Find the topmost shape at a given point.
 * Shapes rendered later are on top.
 */
export function findShapeAtPoint(shapes, px, py) {
    // Iterate from end (topmost) to start (bottommost)
    for (let i = shapes.length - 1; i >= 0; i--) {
        if (hitTestShape(shapes[i], px, py)) {
            return shapes[i];
        }
    }
    return null;
}
